import * as React from "react";
import DateRange, {RangeToTimes} from "../components/DateRange";
import Loading from "../components/loading";
import {
    Alert,
    FormControl,
    FormGroup,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    Link as MuiLink,
    Menu,
    MenuItem,
    Paper,
    Popover,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableSortLabel,
    Tooltip,
    Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HandshakeIcon from "@mui/icons-material/Handshake";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import StripedTableRow from "../components/StripedTableRow";
import TableFooterRow from "../components/TableFooterRow";
import TableHeaderRow from "../components/TableHeaderRow";
import MultiSelect from "../components/multiSelect/multiSelect";
import useDebounce from "../lib/UseDebounce";
import {useAuth} from "../providers/auth";
import {PermissionType} from "../objects/UserRead";
import {toast} from "react-toastify";
import moment from "moment";
import ClickFeedCampaignUpsert from "./clickFeedCampaignUpsert";
import {
    downloadClickFeedCampaignsCsv,
    loadClickFeedCampaignsPage,
    newClickFeedCampaignDefaults,
    searchClickFeedCampaigns,
    setClickFeedCampaignActive,
} from "./clickFeedCampaignsService";

function money(n: number) {
    return "$" + (n || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

function moneyWhole(n: number) {
    return "$" + (n || 0).toLocaleString(undefined, {maximumFractionDigits: 0});
}

function capCellSx(row: any, isToday: boolean) {
    if (!isToday) {
        return undefined;
    }
    if (row.dailyCap == 0 || (row.dailyCap > 0 && (row.revenue || 0) >= row.dailyCap)) {
        return {color: "error.main", fontWeight: "bold"};
    }
    if (row.dailyCap > 0 && (row.revenue || 0) >= row.dailyCap * 0.9) {
        return {color: "warning.main", fontWeight: "bold"};
    }
    return undefined;
}

const EPC_SOURCE_META: Record<string, {label: string; sx: any}> = {
    realtime: {
        label: "Realtime (today)",
        sx: undefined,
    },
    daily: {
        label: "Daily (last 3 days)",
        sx: {color: "info.main"},
    },
    manual: {
        label: "Manual (default RPM / 1000)",
        sx: {color: "warning.main"},
    },
};

function epcSourceOf(row: any): "realtime" | "daily" | "manual" {
    if (row.epcSource === "realtime" || row.epcSource === "daily" || row.epcSource === "manual") {
        return row.epcSource;
    }
    return row.manualEpc ? "manual" : "realtime";
}

export default function ClickFeedCampaignsPage() {
    const auth = useAuth();
    const isAdmin = !!auth.user?.permissions?.includes(PermissionType.Admin);
    const canWrite = !!auth.user?.permissions?.includes(PermissionType.CF) || isAdmin;
    const canFullCf = canWrite; // CF / Admin: full columns; CFRead-only sales reps get the reduced grid

    const [dateRange, setDateRange] = React.useState([] as any[]);
    const [loading, setLoading] = React.useState(true);
    const [lookupsReady, setLookupsReady] = React.useState(false);
    const [rows, setRows] = React.useState([] as any[]);
    const [count, setCount] = React.useState(0);
    const [search, setSearch] = React.useState("");
    const [activeFilter, setActiveFilter] = React.useState<"all" | "active" | "inactive">("active");
    const [selectedCompanies, setSelectedCompanies] = React.useState([] as any[]);
    const [companies, setCompanies] = React.useState([] as any[]);
    const [publishers, setPublishers] = React.useState([] as {id: number; name: string}[]);
    const [programs, setPrograms] = React.useState([] as any[]);
    const [trafficTypes, setTrafficTypes] = React.useState([] as string[]);
    const [editCampaign, setEditCampaign] = React.useState<any>(null);
    const [anchorEls, setAnchorEls] = React.useState<any>({});
    const [epcHelpAnchor, setEpcHelpAnchor] = React.useState<HTMLElement | null>(null);
    const [sort, setSort] = React.useState({column: "id", direction: "desc" as "asc" | "desc"});

    const getCompanies = async (searchText: string) => {
        const s = (searchText || "").toLowerCase();
        return companies.filter((c) => !s || String(c.value).toLowerCase().includes(s));
    };

    React.useEffect(() => {
        document.title = "Click Feed Campaigns | DN";
        const init = async () => {
            const d = RangeToTimes("Today");
            setDateRange(d);
            setLoading(true);
            try {
                const startDate = moment(d[0]).format("YYYY-MM-DD HH:mm:ss");
                const endDate = moment(d[1]).format("YYYY-MM-DD HH:mm:ss");
                const {lookups, campaigns} = await loadClickFeedCampaignsPage({
                    includeCompanies: isAdmin,
                    includeEditLookups: canWrite,
                    campaigns: {
                        startDate,
                        endDate,
                        active: true,
                        limit: 10000,
                    },
                });
                setCompanies(lookups.companies);
                setPublishers(lookups.publishers);
                setPrograms(lookups.programs);
                setTrafficTypes(lookups.trafficTypes);
                setRows(campaigns.items || []);
                setCount(campaigns.count || 0);
                setLookupsReady(true);
            } catch (e: any) {
                toast.error(e?.message || "Failed to load campaigns");
                setRows([]);
                setCount(0);
                setLookupsReady(true);
            } finally {
                setLoading(false);
            }
        };
        init().then(() => {});
    }, []);

    const reload = async (
        range: any[],
        sSearch: string,
        active: "all" | "active" | "inactive",
        companySel: any[],
    ) => {
        if (!range || range.length < 2) {
            return;
        }
        setLoading(true);
        try {
            const startDate = moment(range[0]).format("YYYY-MM-DD HH:mm:ss");
            const endDate = moment(range[1]).format("YYYY-MM-DD HH:mm:ss");
            const result = await searchClickFeedCampaigns({
                search: sSearch.trim() || undefined,
                startDate,
                endDate,
                companyIds: companySel.map((c) => Number(c.id)),
                active: active === "all" ? null : active === "active",
                limit: 10000,
            });
            setRows(result.items || []);
            setCount(result.count || 0);
        } catch (e: any) {
            toast.error(e?.message || "Failed to load campaigns");
            setRows([]);
            setCount(0);
        } finally {
            setLoading(false);
        }
    };

    useDebounce(() => {
        if (!lookupsReady || !dateRange?.length) {
            return;
        }
        reload(dateRange, search, activeFilter, selectedCompanies).then(() => {});
    }, [search], 400);

    const updateDateRange = async (d: any) => {
        setDateRange(d);
        await reload(d, search, activeFilter, selectedCompanies);
    };

    const handleActiveFilter = async (event: SelectChangeEvent) => {
        const v = event.target.value as "all" | "active" | "inactive";
        setActiveFilter(v);
        await reload(dateRange, search, v, selectedCompanies);
    };

    const isToday = React.useMemo(() => {
        if (!dateRange?.[0]) {
            return false;
        }
        return moment(dateRange[0]).isSame(moment(), "day") && moment(dateRange[1]).isSame(moment(), "day");
    }, [dateRange]);

    const totals = React.useMemo(() => {
        return rows.reduce((acc, r) => {
            acc.impressions += r.impressions || 0;
            acc.revenue += r.revenue || 0;
            acc.revenueMonthly += r.revenueMonthly || 0;
            return acc;
        }, {impressions: 0, revenue: 0, revenueMonthly: 0});
    }, [rows]);

    const weightedEpc = React.useMemo(() => {
        let weight = 0;
        let sum = 0;
        for (const r of rows) {
            const imp = r.impressions || 0;
            weight += imp;
            sum += (r.epc || 0) * imp;
        }
        return weight > 0 ? sum / weight : 0;
    }, [rows]);

    const sortValue = (row: any, column: string) => {
        switch (column) {
            case "company": return (row.company?.name || "").toLowerCase();
            case "broker": return (row.broker?.name || "").toLowerCase();
            case "name": return (row.name || "").toLowerCase();
            case "url": return (row.url || "").toLowerCase();
            case "categories": return (row.categories || []).join(",").toLowerCase();
            case "publisherIds": return (row.publisherIds || []).length;
            case "active": return row.active ? 1 : 0;
            default: return row[column] ?? 0;
        }
    };

    const TEXT_SORT_COLUMNS = new Set(["company", "broker", "name", "url", "categories"]);

    const doSort = (column: string) => {
        if (sort.column === column) {
            setSort({column, direction: sort.direction === "desc" ? "asc" : "desc"});
            return;
        }
        setSort({
            column,
            direction: TEXT_SORT_COLUMNS.has(column) ? "asc" : "desc",
        });
    };

    const sortedRows = React.useMemo(() => {
        const copy = [...rows];
        copy.sort((a, b) => {
            const av = sortValue(a, sort.column);
            const bv = sortValue(b, sort.column);
            if (av < bv) {
                return sort.direction === "asc" ? -1 : 1;
            }
            if (av > bv) {
                return sort.direction === "asc" ? 1 : -1;
            }
            return 0;
        });
        return copy;
    }, [rows, sort]);

    const sortLabel = (label: React.ReactNode, column: string, alignRight = false) => (
        <TableSortLabel
            sx={{width: "100%", justifyContent: alignRight ? "flex-end" : undefined}}
            active={sort.column === column}
            direction={sort.column === column && sort.direction === "desc" ? "desc" : "asc"}
            onClick={() => doSort(column)}
        >
            {label}
        </TableSortLabel>
    );

    const openMenu = (id: number, el: HTMLElement) => {
        setAnchorEls((prev: any) => ({...prev, [id]: el}));
    };
    const closeMenu = (id: number) => {
        setAnchorEls((prev: any) => ({...prev, [id]: null}));
    };

    const toggleActive = async (row: any, isActive: boolean) => {
        closeMenu(row.id);
        try {
            await setClickFeedCampaignActive(row.id, isActive);
            toast.success(isActive ? "Activated" : "Deactivated");
            await reload(dateRange, search, activeFilter, selectedCompanies);
        } catch (e: any) {
            toast.error(e?.message || "Failed to update active");
        }
    };

    const formatListWithExtra = (items: string[], keepVisible = 1) => {
        if (!items.length) {
            return {visible: [] as string[], extra: 0, title: ""};
        }
        const keep = Math.max(1, keepVisible);
        if (items.length <= keep) {
            return {visible: items, extra: 0, title: items.join(", ")};
        }
        return {
            visible: items.slice(0, keep),
            extra: items.length - keep,
            title: items.join(", "),
        };
    };

    const renderListWithExtra = (items: string[], color?: string, keepVisible = 1) => {
        const list = formatListWithExtra(items, keepVisible);
        if (!list.visible.length) {
            return null;
        }
        return (
            <Tooltip title={list.title}>
                <Box sx={{
                    display: "flex",
                    alignItems: "baseline",
                    maxWidth: 200,
                    color: color || undefined,
                }}>
                    <Box component="span" sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                    }}>
                        {list.visible.join(", ")}
                    </Box>
                    {list.extra > 0 && (
                        <Box component="span" sx={{flexShrink: 0, whiteSpace: "nowrap", pl: 0.5}}>
                            +{list.extra}
                        </Box>
                    )}
                </Box>
            </Tooltip>
        );
    };

    const renderPublisherIds = (publisherIds: number[], allowed?: boolean) => {
        const ids = (publisherIds || []).map(Number).filter((id) => Number.isFinite(id) && id > 0);
        if (!ids.length) {
            return null;
        }
        const byId = new Map(publishers.map((p) => [p.id, p.name]));
        const title = ids.map((id) => byId.get(id) || String(id)).join(", ");
        return (
            <Tooltip title={title}>
                <Box
                    component="span"
                    sx={{
                        whiteSpace: "nowrap",
                        cursor: "default",
                        color: allowed ? "success.main" : "error.main",
                    }}
                >
                    {ids.length}
                </Box>
            </Tooltip>
        );
    };

    return (
        <>
            <h2>Click Feed Campaigns</h2>
            <FormGroup row={true} sx={{alignItems: "center", gap: 1, flexWrap: "wrap"}}>
                {lookupsReady && (
                    <DateRange showTime={false} dateRange={dateRange} setDateRange={updateDateRange} style={{marginRight: "10px"}}/>
                )}
                {isAdmin && lookupsReady && (
                    <FormControl sx={{m: 1, minWidth: 220}} size="small">
                        <MultiSelect
                            searchFunction={getCompanies}
                            selectedItems={selectedCompanies}
                            setSelectedItems={(items: any[]) => {
                                setSelectedCompanies(items);
                                reload(dateRange, search, activeFilter, items).then(() => {});
                            }}
                            placeholder="Companies"
                        />
                    </FormControl>
                )}
                <FormControl sx={{m: 1, minWidth: 140}} size="small">
                    <InputLabel id="cf-active-filter">Active</InputLabel>
                    <Select labelId="cf-active-filter" label="Active" value={activeFilter} onChange={handleActiveFilter}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                </FormControl>
                <FormControl variant="outlined" sx={{m: 1, minWidth: 180}} size="small">
                    <Input
                        id="cf-search"
                        type="text"
                        spellCheck={false}
                        placeholder="Search"
                        value={search}
                        onChange={(e: any) => setSearch(e.target.value)}
                        startAdornment={<InputAdornment position="start"><SearchIcon/></InputAdornment>}
                    />
                </FormControl>
                <FormControl sx={{m: 1}} size="small">
                    <Button variant="contained" color="primary"
                            onClick={() => reload(dateRange, search, activeFilter, selectedCompanies)}>
                        Reload
                    </Button>
                </FormControl>
                {canFullCf && !!rows.length && !loading && (
                    <FormControl sx={{m: 1}} size="small">
                        <Button variant="contained" color="success" onClick={() => downloadClickFeedCampaignsCsv(sortedRows)}>
                            <FileDownloadIcon/>&nbsp;Export
                        </Button>
                    </FormControl>
                )}
                {canWrite && (
                    <FormControl sx={{m: 1}} size="small">
                        <Button variant="contained" color="warning"
                                onClick={() => setEditCampaign(newClickFeedCampaignDefaults())}>
                            <AddBoxOutlinedIcon/>&nbsp;Create Campaign
                        </Button>
                    </FormControl>
                )}
            </FormGroup>

            {loading || !lookupsReady ? <Loading/> : (
                <>
                    {!rows.length ? <Alert severity="warning">No results found</Alert> : (
                        <>
                            <Box sx={{margin: "10px 0", fontWeight: "bold", fontSize: "13px"}}>
                                Showing {rows.length.toLocaleString()} of {count.toLocaleString()} campaigns.
                            </Box>
                            <TableContainer component={Paper} sx={{maxWidth: "calc(100vw - 10px)"}}>
                                <Table size="small">
                                    <TableHead>
                                        <TableHeaderRow>
                                            <TableCell>{sortLabel("Id", "id")}</TableCell>
                                            <TableCell>{sortLabel("Cmp", "company")}</TableCell>
                                            <TableCell>{sortLabel("Broker", "broker")}</TableCell>
                                            <TableCell>{sortLabel("App Id", "name")}</TableCell>
                                            <TableCell>{sortLabel("Url", "url")}</TableCell>
                                            <TableCell>{sortLabel("CF Id", "hp_campaign_id")}</TableCell>
                                            {canFullCf && <TableCell>{sortLabel("PR Id", "hp_campaign_id_pr")}</TableCell>}
                                            {canFullCf && <TableCell>{sortLabel("Categories", "categories")}</TableCell>}
                                            {canFullCf && <TableCell>{sortLabel("Pubs", "publisherIds")}</TableCell>}
                                            <TableCell>{sortLabel("Active", "active")}</TableCell>
                                            <TableCell align="right">{sortLabel("Imp", "impressions", true)}</TableCell>
                                            <TableCell align="right">{sortLabel("Rev", "revenue", true)}</TableCell>
                                            <TableCell align="right">{sortLabel("Daily Cap", "dailyCap", true)}</TableCell>
                                            <TableCell align="right">{sortLabel("Rev M", "revenueMonthly", true)}</TableCell>
                                            <TableCell align="right">{sortLabel("Monthly Cap", "monthlyCap", true)}</TableCell>
                                            <TableCell align="right">{sortLabel("RPM", "rpm", true)}</TableCell>
                                            {canFullCf && (
                                            <TableCell align="right" sx={{whiteSpace: "nowrap"}}>
                                                <Box sx={{display: "inline-flex", alignItems: "center"}}>
                                                    {sortLabel("EPC", "epc", true)}
                                                    <IconButton
                                                        size="small"
                                                        aria-label="How EPC is calculated"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEpcHelpAnchor(e.currentTarget);
                                                        }}
                                                        sx={{p: 0.25, ml: 0.25, verticalAlign: "middle"}}
                                                    >
                                                        <HelpOutlineIcon sx={{fontSize: 16}}/>
                                                    </IconButton>
                                                </Box>
                                                <Popover
                                                    open={Boolean(epcHelpAnchor)}
                                                    anchorEl={epcHelpAnchor}
                                                    onClose={() => setEpcHelpAnchor(null)}
                                                    anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                                                    transformOrigin={{vertical: "top", horizontal: "right"}}
                                                >
                                                    <Box sx={{p: 1.5, maxWidth: 360, fontSize: 13}}>
                                                        <Typography variant="subtitle2" sx={{mb: 1}}>How EPC is calculated</Typography>
                                                        <Box sx={{mb: 1}}>
                                                            <Box component="span" sx={{fontWeight: 600}}>Realtime</Box>
                                                            {" — default text color. Campaign reports realtime revenue: today revenue / unique clicks."}
                                                        </Box>
                                                        <Box sx={{mb: 1, color: "info.main"}}>
                                                            <Box component="span" sx={{fontWeight: 600}}>Daily</Box>
                                                            {" — blue. Campaign reports daily revenue: last 3 days revenue / unique clicks (within the current month)."}
                                                        </Box>
                                                        <Box sx={{color: "warning.main"}}>
                                                            <Box component="span" sx={{fontWeight: 600}}>Manual</Box>
                                                            {" — yellow. No recent reported revenue: default RPM / 1000 (set on the campaign)."}
                                                        </Box>
                                                    </Box>
                                                </Popover>
                                            </TableCell>
                                            )}
                                            {canFullCf && <TableCell/>}
                                        </TableHeaderRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedRows.map((row) => (
                                            <StripedTableRow key={row.id}>
                                                <TableCell>
                                                    {canFullCf && row.managed && (
                                                        <Tooltip title="Managed self-serve">
                                                            <HandshakeIcon fontSize="small" sx={{mr: 0.5, verticalAlign: "middle"}}/>
                                                        </Tooltip>
                                                    )}
                                                    {row.id}
                                                </TableCell>
                                                <TableCell>{row.company?.name}</TableCell>
                                                <TableCell>{row.broker?.name}</TableCell>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell sx={{maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                                    {row.previewUrl ? (
                                                        <MuiLink href={row.previewUrl} target="_blank" rel="noreferrer">
                                                            {row.url}
                                                        </MuiLink>
                                                    ) : row.url}
                                                </TableCell>
                                                <TableCell sx={{color: row.cfActive ? undefined : "error.main"}}>
                                                    {row.hp_campaign_id ? (
                                                        <MuiLink href={`/campaigns/${row.hp_campaign_id}`} target="_blank" rel="noreferrer" sx={{color: "inherit"}}>
                                                            {row.hp_campaign_id}
                                                        </MuiLink>
                                                    ) : null}
                                                </TableCell>
                                                {canFullCf && (
                                                    <TableCell sx={{color: row.prActive ? undefined : "error.main"}}>
                                                        {row.hp_campaign_id_pr ? (
                                                            <MuiLink href={`/campaigns/${row.hp_campaign_id_pr}`} target="_blank" rel="noreferrer" sx={{color: "inherit"}}>
                                                                {row.hp_campaign_id_pr}
                                                            </MuiLink>
                                                        ) : null}
                                                    </TableCell>
                                                )}
                                                {canFullCf && (
                                                    <TableCell>
                                                        {renderListWithExtra(row.categories || [])}
                                                    </TableCell>
                                                )}
                                                {canFullCf && (
                                                    <TableCell sx={{whiteSpace: "nowrap"}}>
                                                        {renderPublisherIds(row.publisherIds || [], !!row.publishersAllowed)}
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    {row.active
                                                        ? <CheckCircleOutlineIcon sx={{color: "green", fontSize: "20px", verticalAlign: "text-top"}}/>
                                                        : <HighlightOffIcon sx={{color: "red", fontSize: "20px", verticalAlign: "text-top"}}/>}
                                                </TableCell>
                                                <TableCell align="right">{(row.impressions || 0).toLocaleString()}</TableCell>
                                                <TableCell align="right">{money(row.revenue)}</TableCell>
                                                <TableCell align="right" sx={capCellSx(row, isToday)}>
                                                    {moneyWhole(row.dailyCap)}
                                                </TableCell>
                                                <TableCell align="right">{money(row.revenueMonthly)}</TableCell>
                                                <TableCell align="right" sx={
                                                    row.monthlyCap > 0 && (row.revenueMonthly || 0) >= row.monthlyCap
                                                        ? {color: "error.main", fontWeight: "bold"}
                                                        : undefined
                                                }>
                                                    {moneyWhole(row.monthlyCap)}
                                                </TableCell>
                                                <TableCell align="right">{money(row.rpm)}</TableCell>
                                                {canFullCf && (
                                                    <TableCell align="right" sx={EPC_SOURCE_META[epcSourceOf(row)]?.sx}>
                                                        <Tooltip title={EPC_SOURCE_META[epcSourceOf(row)]?.label || "EPC"}>
                                                            <Box component="span">{money(row.epc)}</Box>
                                                        </Tooltip>
                                                    </TableCell>
                                                )}
                                                {canFullCf && (
                                                <TableCell>
                                                    {canWrite && (
                                                        <>
                                                            <IconButton size="small"
                                                                        onClick={(e) => openMenu(row.id, e.currentTarget)}>
                                                                <MoreVertIcon fontSize="small"/>
                                                            </IconButton>
                                                            <Menu
                                                                anchorEl={anchorEls[row.id]}
                                                                open={Boolean(anchorEls[row.id])}
                                                                onClose={() => closeMenu(row.id)}
                                                            >
                                                                <MenuItem onClick={() => {
                                                                    closeMenu(row.id);
                                                                    setEditCampaign(row);
                                                                }}>
                                                                    <EditIcon fontSize="small" sx={{mr: 1}}/> Edit
                                                                </MenuItem>
                                                                {!row.managed && row.active && (
                                                                    <MenuItem onClick={() => toggleActive(row, false)}>
                                                                        <HighlightOffIcon fontSize="small" sx={{mr: 1}}/> Deactivate
                                                                    </MenuItem>
                                                                )}
                                                                {!row.managed && !row.active && (
                                                                    <MenuItem onClick={() => toggleActive(row, true)}>
                                                                        <DoneOutlineIcon fontSize="small" sx={{mr: 1}}/> Activate
                                                                    </MenuItem>
                                                                )}
                                                            </Menu>
                                                        </>
                                                    )}
                                                </TableCell>
                                                )}
                                            </StripedTableRow>
                                        ))}
                                    </TableBody>
                                    <TableFooter>
                                        <TableFooterRow>
                                            <TableCell colSpan={canFullCf ? 10 : 7}>Totals</TableCell>
                                            <TableCell align="right">{totals.impressions.toLocaleString()}</TableCell>
                                            <TableCell align="right">{money(totals.revenue)}</TableCell>
                                            <TableCell/>
                                            <TableCell align="right">{money(totals.revenueMonthly)}</TableCell>
                                            <TableCell/>
                                            <TableCell align="right">
                                                {totals.impressions > 0
                                                    ? money((totals.revenue / totals.impressions) * 1000)
                                                    : "0.00"}
                                            </TableCell>
                                            {canFullCf && <TableCell align="right">{money(weightedEpc)}</TableCell>}
                                            {canFullCf && <TableCell/>}
                                        </TableFooterRow>
                                    </TableFooter>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </>
            )}

            {editCampaign && (
                <ClickFeedCampaignUpsert
                    campaign={editCampaign}
                    setCampaign={setEditCampaign}
                    reload={() => reload(dateRange, search, activeFilter, selectedCompanies)}
                    publishers={publishers}
                    programs={programs}
                    trafficTypes={trafficTypes}
                />
            )}
        </>
    );
}
